package co.maytes.standupbot.dashboard.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.Disclosure
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.src
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

enum class Brand(val label: String, val tintClass: String, val asset: String) {
    Linear("Linear", "mk-tint--linear", "/brand/linear.svg"),
    GitHub("GitHub", "mk-tint--github", "/brand/github.svg"),
    Claude("Claude", "mk-tint--claude", "/brand/claude.svg"),
    Slack("Slack", "mk-tint--slack", "/brand/slack.svg"),
}

@Composable
fun BrandGlyph(brand: Brand) {
    when (brand) {
        Brand.GitHub -> Span(attrs = { classes("mk-glyph", "mk-glyph--github"); attr("aria-hidden", "true") })
        else -> Img(src = brand.asset, attrs = { classes("mk-glyph"); alt("") })
    }
}

@Composable
fun BrandBadge(brand: Brand, large: Boolean = false) {
    Span(
        attrs = {
            classes("mk-tile", brand.tintClass)
            if (large) classes("mk-tile--lg")
        },
    ) { BrandGlyph(brand) }
}

@Composable
fun MkIcon(icon: MarketingIcon) {
    Span(attrs = { classes("mk-ico", "mk-ico--${icon.slug}"); attr("aria-hidden", "true") })
}

@Composable
fun IconTile(icon: MarketingIcon, tintClass: String, large: Boolean = false) {
    Span(
        attrs = {
            classes("mk-tile", tintClass)
            if (large) classes("mk-tile--lg")
        },
    ) { MkIcon(icon) }
}

@Composable
fun SectionHead(title: String, line: String) {
    Div(attrs = { classes("mk-section-head") }) {
        Reveal(index = 0) {
            H2(attrs = { classes("mk-h2") }) { Text(title) }
        }
        Reveal(index = 1) {
            P(attrs = { classes("mk-lede") }) { Text(line) }
        }
    }
}

@Composable
fun PageIntro(title: String, line: String) {
    Section(attrs = { classes("mk-intro") }) {
        Div(attrs = { classes("mk-wrap", "mk-intro-inner") }) {
            Entering(0) { H1(attrs = { classes("mk-h1") }) { Text(title) } }
            Entering(1) { P(attrs = { classes("mk-lede") }) { Text(line) } }
        }
    }
}

@Composable
private fun Entering(index: Int, content: @Composable () -> Unit) {
    Div(
        attrs = {
            classes(Marketing.ENTER_CLASS)
            style { property(Marketing.INDEX_VAR, "$index") }
        },
    ) { content() }
}

@Composable
fun Fold(
    title: String,
    modifier: Modifier = Modifier,
    leading: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    Div(attrs = modifier.toAttrs { classes("mk-fold"); if (open) classes("mk-fold--open") }) {
        Button(
            attrs = Modifier
                .onClick { open = !open }
                .toAttrs {
                    classes("mk-fold-head")
                    type(ButtonType.Button)
                    attr("aria-expanded", "$open")
                },
        ) {
            leading?.invoke()
            Span(attrs = { classes("mk-fold-title") }) { Text(title) }
            MkIcon(MarketingIcon.Chevron)
        }
        Disclosure(open = open) {
            Div(attrs = { classes("mk-fold-body") }) { content() }
        }
    }
}
